import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Container, LayoutStandard } from "components";
import { AuthorityConstants, RouteConstants } from "constant";
import { UserService } from "services";
import { ClientStatus, CreditStatus } from "types";
import { AppUtils, UserUtils } from "utils";

import ClientCreditStatusFilterBar, {
  getDefaultCreditRequestStatus,
} from "../../clients/list/ClientCreditStatusFilterBar";
import ClientStatusFilterBar from "../../clients/list/ClientStatusFilterBar";
import ClientsList from "../../clients/list/list";
import TuttiClientsList from "../../clients/list/TuttiClientsList";

const CLientsMyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientStatus, setClientStatus] = useState<ClientStatus>("ACTIVE");
  const [creditRequestStatus, setCreditRequestStatus] = useState<CreditStatus | undefined>(
    getDefaultCreditRequestStatus("ACTIVE"),
  );
  const isTuttiMode = useMemo(
    () => searchParams.get("source")?.toLowerCase() === "tutti",
    [searchParams],
  );

  const handleClientStatusChange = useCallback((status: ClientStatus) => {
    setClientStatus(status);
    setCreditRequestStatus(getDefaultCreditRequestStatus(status));
  }, []);

  const fetch = useCallback((page: number, search: string | undefined, source?: string) => {
    UserService.userClients({
      page,
      search,
      phase: "ALL",
      source,
      clientStatus,
      creditRequestStatus,
      agent: true,
    });
  }, [clientStatus, creditRequestStatus]);

  const canCreateClient = useMemo(() => UserUtils.hasAuthority(AuthorityConstants.ROLE_CR_C), []);
  const canViewTutti = useMemo(() => UserUtils.hasAnyAuthority([
    AuthorityConstants.ROLE_TUTTI_R,
    AuthorityConstants.ROLE_TUTTI_C,
  ]), []);
  const canUploadTutti = useMemo(() => UserUtils.hasAuthority(AuthorityConstants.ROLE_TUTTI_C), []);
  const addNew = useCallback(() => navigate(RouteConstants.ROUTE_CLIENTS_NEW), [navigate]);

  return (
    <LayoutStandard
      authority={AuthorityConstants.ROLE_CR}
      title={AppUtils.composeTabTitle(t("clients.list.title"))}
    >
      <Container>
        {!isTuttiMode ? (
          <>
            <ClientStatusFilterBar value={clientStatus} onChange={handleClientStatusChange} />
            <ClientCreditStatusFilterBar
              clientStatus={clientStatus}
              value={creditRequestStatus}
              onChange={setCreditRequestStatus}
            />
            <ClientsList
              fetch={fetch}
              addNew={canCreateClient ? addNew : undefined}
              clientStatus={clientStatus}
              creditRequestStatus={creditRequestStatus}
            />
          </>
        ) : (
          <TuttiClientsList
            canViewTutti={canViewTutti}
            showUploadButton={canUploadTutti}
          />
        )}
      </Container>
    </LayoutStandard>
  );
};

export default CLientsMyPage;
